// Decision steps on the task page: the workflow variables panel, the model
// level badge, and the timeline card of a decision with its "Change decision"
// dialog (docs/plans/2026-10-02-beslisstap-contract.md §8 S7).

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type {
  DecisionOverrideRequest,
  Task,
  TaskTransition,
  WorkflowDefinition,
  WorkflowState,
  WorkflowVariable,
} from '@goblic/shared'
import { ApiError, tasksApi } from '../../lib/api-client'
import { formatDateTime, formatRelative, humanize } from '../../lib/format'
import { Dialog, Field, Notice } from '../ui'

const SOURCE_LABEL: Record<WorkflowVariable['source']['kind'], string> = {
  decision: 'Decision',
  option: 'Chosen option',
  override: 'Changed by a person',
}

/** Where a variable came from: the step that set it, as readable text. */
function sourceText(variable: WorkflowVariable): string {
  return `${SOURCE_LABEL[variable.source.kind] ?? humanize(variable.source.kind)}, ${humanize(variable.source.state)}`
}

/** `nextWorkflow.workflow` / `.state` as readable text ('@default' = the project's default workflow, '@inbox' = its queue). */
export function describeNextWorkflow(next: { workflow: string; state?: string | null }): string {
  const workflow =
    next.workflow === '@default' ? 'the project’s default workflow' : humanize(next.workflow)
  if (!next.state) return `Continues in ${workflow}`
  const state = next.state === '@inbox' ? 'its Inbox' : humanize(next.state)
  return `Continues in ${workflow}, ${state}`
}

/** The model level a decision gave the task, shown next to the status badges. */
export function ModelTierBadge({ task }: { task: Task }) {
  if (!task.modelTier) return null
  return (
    <span
      className="rounded-full border border-line px-2 py-0.5 text-sm text-ink-2"
      title="Model level for this task’s runs, set by a decision step"
    >
      Model level: {task.modelTier}
    </span>
  )
}

/** What the task's decision steps decided so far; later steps can read these. Renders nothing while there are none. */
export function WorkflowVariablesPanel({ task }: { task: Task }) {
  const entries = Object.entries(task.workflowVars ?? {})
  if (entries.length === 0) return null
  return (
    <section>
      <h3 className="mb-1.5 text-sm font-medium text-ink-3">
        Workflow variables
      </h3>
      <div className="overflow-x-auto rounded-control border border-line">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Value</th>
              <th>Reason</th>
              <th>Source</th>
              <th>Set</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([name, variable]) => (
              <tr key={name}>
                <td className="font-mono text-sm">{name}</td>
                <td className="font-mono text-sm font-medium text-ink">
                  {variable.value}
                </td>
                <td className="whitespace-pre-wrap text-ink-2">
                  {variable.reason || '—'}
                </td>
                <td className="whitespace-nowrap">
                  {variable.source.runId ? (
                    <Link
                      to="/runs/$runId"
                      params={{ runId: variable.source.runId }}
                      className="link"
                    >
                      {sourceText(variable)}
                    </Link>
                  ) : (
                    sourceText(variable)
                  )}
                </td>
                <td
                  className="whitespace-nowrap"
                  title={formatDateTime(variable.setAt)}
                >
                  {formatRelative(variable.setAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/**
 * The engine writes a decision's transition reason as
 * `<variable> = <choice>: <reason>`, or `<variable> = <choice> (<why the
 * default was used>)` for a fallback.
 */
export function parseDecisionReason(reason: string | null): {
  variable: string
  value: string
  reason: string | null
  fallback: boolean
} | null {
  const match = reason?.match(
    /^([a-z][a-z0-9_]*) = ([a-z0-9_-]+)(?::\s*([\s\S]*)| \(([\s\S]*)\))?$/
  )
  if (!match) return null
  const fallbackNote = match[4]?.replace(/^(?:standaard|default):\s*/i, '').trim()
  return {
    variable: match[1]!,
    value: match[2]!,
    reason: match[3]?.trim() || fallbackNote || null,
    fallback: match[4] != null,
  }
}

/**
 * What a decision card shows as the answer. The latest decision (`canChange`)
 * shows what the task holds now (a person's override is its own transition,
 * so the parsed reason can be out of date); older ones show their parsed value.
 */
export function decisionCardValue(
  task: Pick<Task, 'workflowVars'>,
  variable: string,
  parsed: { value: string } | null,
  canChange: boolean
): { live: WorkflowVariable | undefined; shownValue: string | null; overridden: boolean } {
  const live = canChange ? task.workflowVars?.[variable] : undefined
  return {
    live,
    shownValue: live?.value ?? parsed?.value ?? null,
    overridden: live?.source.kind === 'override',
  }
}

/** Body of `POST /tasks/:id/decision-override`: the note is trimmed, an empty one becomes null. */
export function buildOverrideBody(state: string, option: string, note: string): DecisionOverrideRequest {
  return { state, option, note: note.trim() || null }
}

/** The decision step a transition left, found in the project's workflows. Prefers the workflow the task was in when it entered that state. */
export function findDecisionState(
  workflows: WorkflowDefinition[] | undefined,
  state: string | null,
  preferredKey: string | null | undefined
): WorkflowState | null {
  if (!state || !workflows) return null
  const isIt = (s: WorkflowState) => s.name === state && s.kind === 'decision'
  const preferred = workflows.find((w) => w.key === preferredKey)?.states.find(isIt)
  if (preferred) return preferred
  for (const w of workflows) {
    const found = w.states.find(isIt)
    if (found) return found
  }
  return null
}

/** One decision in the workflow history: the answer, why, and (for the latest one) a way to change it. */
export function DecisionCard({
  task,
  transition,
  decisionState,
  canChange,
}: {
  task: Task
  transition: TaskTransition
  /** The decision step it came from, when the project's workflows are loaded. */
  decisionState: WorkflowState | null
  canChange: boolean
}) {
  const [changing, setChanging] = useState(false)
  const parsed = parseDecisionReason(transition.reason)
  const variable = parsed?.variable ?? decisionState?.variable ?? 'decision'
  // The latest decision shows what the task holds now: a person's override
  // is logged as its own transition, so the parsed reason can be out of date.
  const { live, shownValue, overridden } = decisionCardValue(task, variable, parsed, canChange)
  return (
    <div className="mt-1 rounded-control border border-line bg-surface-2/40 px-3 py-2">
      <p className="text-ink">
        <span className="text-ink-3">Decision: </span>
        <span className="font-mono">{variable}</span>
        {shownValue != null ? (
          <>
            {' = '}
            <span className="font-mono font-medium">{shownValue}</span>
            {overridden ? <span className="text-ink-3"> (changed by a person)</span> : null}
          </>
        ) : null}
      </p>
      {overridden && live?.reason ? (
        <p className="whitespace-pre-wrap text-ink-2">“{live.reason}”</p>
      ) : parsed?.reason && !overridden ? (
        <p className="whitespace-pre-wrap text-ink-2">
          {parsed.fallback ? 'Default used: ' : ''}“{parsed.reason}”
        </p>
      ) : parsed?.fallback ? (
        <p className="text-ink-2">Default used.</p>
      ) : !parsed && transition.reason ? (
        <p className="whitespace-pre-wrap text-ink-2">{transition.reason}</p>
      ) : null}
      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-ink-3">
        {transition.runId ? (
          <Link
            to="/runs/$runId"
            params={{ runId: transition.runId }}
            className="link"
          >
            Decision run
          </Link>
        ) : null}
        {canChange ? (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => setChanging(true)}
          >
            Change decision
          </button>
        ) : null}
      </p>
      {changing ? (
        <ChangeDecisionDialog
          task={task}
          state={transition.fromState ?? ''}
          decisionState={decisionState}
          currentValue={shownValue}
          onClose={() => setChanging(false)}
        />
      ) : null}
    </div>
  )
}

/** Pick another option of a decision step (plus an optional note); the task moves on from that option. */
function ChangeDecisionDialog({
  task,
  state,
  decisionState,
  currentValue,
  onClose,
}: {
  task: Task
  state: string
  decisionState: WorkflowState | null
  currentValue: string | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const options = decisionState?.options ?? []
  const [option, setOption] = useState('')
  const [note, setNote] = useState('')

  const override = useMutation({
    mutationFn: () =>
      tasksApi.overrideDecision(task.id, buildOverrideBody(state, option, note)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['task', task.id] })
      void queryClient.invalidateQueries({ queryKey: ['runs'] })
      onClose()
    },
  })

  return (
    <Dialog
      open
      onClose={onClose}
      title="Change decision"
      description={`${humanize(state)}${decisionState?.question ? `: ${decisionState.question}` : ''}`}
      size="md"
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (option) override.mutate()
        }}
      >
        <fieldset>
          <legend className="mb-2 text-base font-medium text-ink">
            New answer
          </legend>
          {options.length === 0 ? (
            <p className="text-sm text-ink-3">
              The options of this step couldn’t be loaded.
            </p>
          ) : (
            <div className="space-y-2">
              {options.map((o) => {
                const selected = o.value === option
                return (
                  <label
                    key={o.value}
                    className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                      selected
                        ? 'border-action bg-action/5'
                        : 'border-line hover:border-line-strong'
                    }`}
                  >
                    <input
                      type="radio"
                      name="decision-option"
                      className="mt-1"
                      checked={selected}
                      onChange={() => setOption(o.value)}
                    />
                    <span>
                      <span className="block font-mono text-base font-medium text-ink">
                        {o.value}
                        {o.value === currentValue ? (
                          <span className="ml-2 font-sans text-sm font-normal text-ink-3">
                            current
                          </span>
                        ) : null}
                      </span>
                      {o.description ? (
                        <span className="block text-sm text-ink-3">
                          {o.description}
                        </span>
                      ) : null}
                      <span className="block text-sm text-ink-3">
                        {o.nextWorkflow
                          ? describeNextWorkflow(o.nextWorkflow)
                          : o.next
                            ? `Continues at ${humanize(o.next)}`
                            : null}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          )}
        </fieldset>
        <Field label="Note (optional)" htmlFor="decision-note">
          <input
            id="decision-note"
            className="field-input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        <p className="text-sm text-ink-3">
          A run that is still going on this task is cancelled.
        </p>
        {override.isError ? (
          <Notice tone="error">
            {override.error instanceof ApiError
              ? override.error.message
              : 'Couldn’t change the decision.'}
          </Notice>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={override.isPending || !option || option === currentValue}
          >
            {override.isPending ? 'Changing…' : 'Change decision'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
